import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import ReactDOM from 'react-dom';
import dynamic from 'next/dynamic';
import type {
  Column,
  ColumnInstance,
  Row,
  TableToggleHideAllColumnProps,
} from 'react-table';
import { useGlobalFilter, useSortBy, useTable } from 'react-table';
import { useExportData } from 'react-table-plugins';
import * as Papa from 'papaparse';
import moment from 'moment';
import type { Theme } from '../components/theme';
import { BodyWrap } from '../components/Main';
import {
  Text,
  Badge,
  Box,
  CheckButton,
  DeleteButton,
  Menu,
  MenuItem,
  Table,
  TableHeaders,
  TableHeader,
  TableRow,
  TableCell,
  TimeField,
  FilterBar,
  FilterBarSeparator,
  FilterBarInput,
  FilterBarDatePicker,
  DropdownButton,
  OptimisticCheckbox,
} from '../components/ui';
import type { TableRowProps } from '../components/ui/Table/Table';
import { NotFoundTableRow } from '../components/ui/Table/Table';
import type {
  ReportingTable_BillOfLadingDatumFragment,
  ReportingTable_CargoMeasurementFragment,
  ReportingTable_OrderDetailsFragment,
  ReportingTable_OrderFragment,
  ReportingTable_PortCallFragment,
  ReportingTable_VesselFragment,
} from '../graphql';
import { useOrdersPageQuery } from '../graphql';
import { exhaustiveCheck, isDefined } from '../lib/util';
import {
  getMassUnitName,
  getVolumeUnitName,
  massCapacityUnits,
  volumeCapacityUnits,
} from '../constants/capacityUnits';
import { CoreEventType, PortCallEventType } from '../components/core/TimeData';
import debounce from 'lodash/debounce';
import { dateStringToNumOpt, stringToNumOpt } from '../components/core/util';
import useDropdown from '../components/hooks/useDropdown';
import { useLocalSetting } from '../components/hooks/useLocalConfig';
import { useHotkeys } from 'react-hotkeys-hook';
import { getPortCallTimeSourceType } from '../components/PortCallUpdates';
import { hasFeature, hasUserRight } from '../state/zustand';

interface ReportingTableProps {
  orders: ReportingTable_OrderFragment[];
  ordersLoading: boolean;
  showArchived: boolean;
  setShowArchived: (showArchived: boolean) => void;
}

interface ReportingTableOrderData
  extends Omit<
    ReportingTable_OrderFragment,
    | 'billOfLading'
    | 'cargoMeasurement'
    | 'orderDetails'
    | 'portCallRelations'
    | 'handledByCoa'
    | 'handledBySpot'
    | 'scheduledOrder'
  > {
  billOfLading: ReportingTable_BillOfLadingDatumFragment | null;
  cargoMeasurement: ReportingTable_CargoMeasurementFragment | null;
  orderDetails: ReportingTable_OrderDetailsFragment | null;
  loadPortPortCall: ReportingTable_PortCallFragment | null;
  dischargePortPortCall: ReportingTable_PortCallFragment | null;
  assignment: {
    type: 'tc' | 'coa' | 'spot';
    vessel: ReportingTable_VesselFragment | null;
    shared: boolean;
  } | null;
}

interface ReportingTableRowData {
  order: ReportingTableOrderData;
  filterKeywords: string[];
  isReadOnly: boolean;
}

interface ReportingTableRowProps extends TableRowProps {
  row: Row<ReportingTableRowData>;
}

interface CellRendererProps<ValueType> {
  value: ValueType;
  row: Row<ReportingTableRowData>;
}

const getExportFileBlob = async ({
  columns,
  data,
  fileType,
  fileName,
}: {
  columns: Array<ColumnInstance<ReportingTableRowData>>;
  data: Array<Array<unknown>>;
  fileType: string;
  fileName: string;
}) => {
  if (fileType === 'csv') {
    // CSV export
    const headerNames = columns.map((col) => col.exportValue);
    const csvString = Papa.unparse({ fields: headerNames, data });
    return new Blob([csvString], { type: 'text/csv' });
  } else if (fileType === 'xlsx') {
    // Excel export
    const header = columns.map((c) => c.exportValue);
    const compatibleData = data.map((row) => {
      const obj: Record<string, unknown> = {};
      header.forEach((col, index) => {
        obj[col] = row[index];
      });
      return obj;
    });

    const XLSX = await import('xlsx');
    const workbook = XLSX.utils.book_new();
    const sheet = XLSX.utils.json_to_sheet(compatibleData, {
      header,
    });

    XLSX.utils.book_append_sheet(workbook, sheet, 'Seaber orders');
    XLSX.writeFile(workbook, `${fileName}.xlsx`);

    // Returning false as downloading of file is already taken care of
    return false;
  }

  return false;
};

const getDateExportValue = (
  date: string | number | null,
  timeZone: string | undefined = 'UTC',
): string => {
  if (!date) return '';
  return moment(date).tz(timeZone).format('yyyy-MM-DD HH:mm (z)');
};

const portCallTimeCellContent = (
  timetype: PortCallEventType,
  isLoadPort: boolean,
  value: number | null,
  row: ReportingTableRowData,
) => {
  const portCall = isLoadPort
    ? row.order.loadPortPortCall
    : row.order.dischargePortPortCall;

  if (!portCall || !value) return '';

  const timeZone = isLoadPort
    ? row.order.loadPort?.timezone
    : row.order.dischargePort?.timezone;

  const sourceType = getPortCallTimeSourceType(portCall, timetype);
  const isActual = sourceType === 'actual';

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
      }}
    >
      <Badge
        isRounded
        variant={isActual ? 'portcalltimestatus/actual' : undefined}
        marginRight={2}
      >
        {sourceType === 'actual'
          ? 'A'
          : sourceType === 'estimate'
            ? 'E'
            : sourceType === 'planned'
              ? 'P'
              : sourceType === 'requested'
                ? 'R'
                : exhaustiveCheck(sourceType)}
      </Badge>
      <TimeField value={value} timeZone={timeZone ?? undefined} />
    </div>
  );
};

const ReportingTableRow = ({
  row,
  ...restProps
}: ReportingTableRowProps): JSX.Element => {
  return (
    <TableRow {...restProps}>
      {row.cells.map((cell) => {
        const { key, ...cellProps } = cell.getCellProps();
        return (
          <TableCell key={key} {...cellProps}>
            {cell.render('Cell')}
          </TableCell>
        );
      })}
    </TableRow>
  );
};

export type GlobalFilter = {
  fromDate: Date | null;
  toDate: Date | null;
  search: string | undefined;
};

export const SearchFilter = ({
  savedKeywords,
  globalFilter,
  setGlobalFilter,
  handleSearchSave,
}: {
  savedKeywords?: string[];
  globalFilter: GlobalFilter;
  setGlobalFilter: (val: GlobalFilter) => void;
  handleSearchSave?: (val: GlobalFilter) => void;
}): JSX.Element => {
  const [searchValue, setSearchValue] = useState(globalFilter.search);
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => setSearchValue(globalFilter.search), [globalFilter.search]);

  const setGlobalFilterDebounced = useMemo(
    () =>
      debounce((val) => {
        setGlobalFilter(val || undefined);
      }, 400),
    [setGlobalFilter],
  );

  const handleSearchChange = useCallback(
    (val: string) => {
      setSearchValue(val);
      setGlobalFilterDebounced({
        ...globalFilter,
        search: val,
      });
    },
    [globalFilter, setGlobalFilterDebounced],
  );

  useHotkeys('esc', () => {
    handleSearchChange('');
  });

  useHotkeys(
    'meta+f, ctrl+f',
    () => {
      inputRef.current?.focus();
    },
    { preventDefault: true },
  );

  const [dropdownRoot, setDropDownRoot] = useState<HTMLElement | null>(null);

  useEffect(() => {
    const el = document.getElementById('dropdown-root');
    setDropDownRoot(el);
  }, []);

  const { setMenuRef, setTargetRef, styles, isVisible, setVisibility } =
    useDropdown();

  return (
    <>
      <Box ref={setTargetRef} position="relative" minWidth={150}>
        <FilterBarInput
          name="search"
          label="Search"
          flexShrink={1}
          ref={inputRef}
          labelMinWidth={54}
          inputProps={{
            onClick: () => setVisibility(true),
            onChange: (e) => handleSearchChange(e.target.value),
            value: searchValue,
            width: '200px',
          }}
          mr={0}
        />

        {!!inputRef.current?.value && (
          <>
            {handleSearchSave && (
              <CheckButton
                position="absolute"
                right={34}
                bottom={2}
                marginBottom={1}
                onPress={() => handleSearchSave(globalFilter)}
              />
            )}
            <DeleteButton
              position="absolute"
              right={12}
              bottom={2}
              marginBottom={1}
              onPress={() => handleSearchChange('')}
            />
          </>
        )}
      </Box>
      {dropdownRoot &&
        ReactDOM.createPortal(
          <div ref={setMenuRef} style={styles}>
            {isVisible && (
              <Box
                backgroundColor="#fff"
                position="absolute"
                top={2}
                left={-208}
                width={200}
                borderRadius={4}
                border={'1 solid #c2c2c2'}
              >
                {savedKeywords?.map((keyword, i) => {
                  return (
                    <Box
                      key={keyword}
                      padding={3}
                      borderBottom={
                        i < savedKeywords.length - 1
                          ? '1px solid #c2c2c2'
                          : 'none'
                      }
                      style={{ cursor: 'pointer' }}
                      onClick={() => handleSearchChange(keyword)}
                    >
                      <Text color="neutral.gray_430">{keyword}</Text>
                    </Box>
                  );
                })}
              </Box>
            )}
          </div>,
          dropdownRoot,
        )}
    </>
  );
};

export type TableColumnsSelectorProps<T extends object> = {
  getToggleHideAllColumnsProps: (
    props?: Partial<TableToggleHideAllColumnProps>,
  ) => TableToggleHideAllColumnProps;
  allColumns: Pick<
    ColumnInstance<T>,
    'getToggleHiddenProps' | 'id' | 'Header'
  >[];
  variant?: keyof Theme['button'];
  children?: React.ReactNode;
};

export function TableColumnsSelector<T extends object>(
  props: TableColumnsSelectorProps<T>,
): JSX.Element {
  const visibleColumns = props.allColumns.filter((col) => col.id !== 'week');

  return (
    <>
      <DropdownButton
        label="Table settings"
        variant={'dark'}
        closeOnContentClick={false}
        minWidth={90}
      >
        <Menu hasShadow>
          <Box
            display="flex"
            flexDirection="column"
            width="100%"
            maxWidth="400px"
          >
            <Box as="label" display="flex" alignItems="center" mb={2}>
              <input
                type="checkbox"
                {...props.getToggleHideAllColumnsProps()}
              />
              Toggle all
            </Box>
            {visibleColumns.map((col) => (
              <Box
                as="label"
                key={col.id}
                display="flex"
                alignItems="center"
                mb={1}
              >
                <>
                  <input type="checkbox" {...col.getToggleHiddenProps()} />
                  {col.Header}
                </>
              </Box>
            ))}
            {props.children}
          </Box>
        </Menu>
      </DropdownButton>
    </>
  );
}

function getOrderContractName(
  order: Omit<
    ReportingTable_OrderFragment,
    | 'billOfLading'
    | 'cargoMeasurement'
    | 'orderDetails'
    | 'portCallRelations'
    | 'handledByCoa'
    | 'handledBySpot'
    | 'scheduledOrder'
  >,
): string | null {
  const contractName =
    order.orderVoyageCharterRelationsByOrderId.nodes[0]
      ?.voyageCharterByVoyageCharterId
      ?.voyageCharterToContractsByVoyageCharterId.nodes[0]?.coaContractByCoaId
      ?.name || null;
  return contractName;
}

export const customPortSortType =
  (
    loadDischarge: CoreEventType.Load | CoreEventType.Discharge,
    eventType: PortCallEventType,
  ) =>
  (
    rowA: Row<ReportingTableRowData>,
    rowB: Row<ReportingTableRowData>,
  ): number => {
    const a =
      rowA.original.order[
        loadDischarge === CoreEventType.Load
          ? 'loadPortPortCall'
          : 'dischargePortPortCall'
      ]?.[
        eventType === PortCallEventType.Arrival
          ? 'plannedArrival'
          : 'plannedDeparture'
      ];
    const b =
      rowB.original.order[
        loadDischarge === CoreEventType.Load
          ? 'loadPortPortCall'
          : 'dischargePortPortCall'
      ]?.[
        eventType === PortCallEventType.Arrival
          ? 'plannedArrival'
          : 'plannedDeparture'
      ];

    const aTime = a ? new Date(a) : new Date();
    const bTime = b ? new Date(b) : new Date();
    return aTime === bTime ? 0 : aTime > bTime ? 1 : -1;
  };

const ReportingTable = ({
  orders,
  ordersLoading,
  showArchived,
  setShowArchived,
}: ReportingTableProps) => {
  const isArchiveViewAvailable = hasFeature('archive_view');

  const data = useMemo<ReportingTableRowData[]>(
    () =>
      orders.map(
        ({
          billOfLading,
          cargoMeasurement,
          orderDetails,
          portCallRelations,
          handledByCoa,
          handledBySpot,
          scheduledOrder,
          ...order
        }): ReportingTableRowData => {
          const loadPortPortCall =
            portCallRelations.nodes.find((rel) => rel?.isLoadPort === true)
              ?.portCall ?? null;

          const dischargePortPortCall =
            portCallRelations.nodes.find((rel) => rel?.isLoadPort === false)
              ?.portCall ?? null;

          const scheduled = scheduledOrder.nodes[0] ?? null;

          const assignment: ReportingTableRowData['order']['assignment'] =
            scheduled
              ? {
                  type: 'tc',
                  vessel: scheduled.vessel ?? null,
                  shared: scheduled.shared,
                }
              : handledBySpot
                ? {
                    type: 'spot',
                    vessel: handledBySpot.vessel ?? null,
                    shared: handledBySpot.shared,
                  }
                : handledByCoa
                  ? {
                      type: 'coa',
                      vessel: handledByCoa.vessel ?? null,
                      shared: handledByCoa.shared,
                    }
                  : null;

          const details = orderDetails?.nodes[0] ?? null;

          const filterKeywords: string[] = [
            order.loadPort?.name ?? '',
            order.dischargePort?.name ?? '',
            order.chartererOrg?.name ?? '',
            assignment?.vessel?.name ?? '',
            details?.receiverOrg?.name ?? '',
            details?.senderOrg?.name ?? '',
            order.assortment?.name ?? '',
            order.id.toString() ?? '',
            order.cargoAmount.toString(),
            order.cargoUnit?.name ?? '',
            assignment?.vessel?.ownerOrg?.name ?? '',
            details?.orderName ?? '',
            order.orderAtributeAssociations.nodes
              ?.map((x) => x?.orderAttribute?.name ?? '')
              .join(' ') ?? '',
            getOrderContractName(order) ?? '',
          ].map((w) => w.toLowerCase());

          const orgIds = order.relatedOrgs?.filter(isDefined) ?? [];
          const isReadOnly = !orgIds.some((orgId) =>
            hasUserRight(orgId, 'createAndEditPortCalls'),
          );

          return {
            order: {
              ...order,
              billOfLading: billOfLading?.nodes[0] ?? null,
              cargoMeasurement: cargoMeasurement?.nodes[0] ?? null,
              orderDetails: details,
              loadPortPortCall,
              dischargePortPortCall,
              assignment: assignment,
            },
            filterKeywords,
            isReadOnly,
          };
        },
      ),
    [orders],
  );

  const columns = useMemo<Array<Column<ReportingTableRowData>>>(
    () => [
      {
        id: 'orderId',
        accessor: (row) => row.order.id,
        Header: 'Id',
      },
      {
        id: 'orderName',
        accessor: (row) => row.order.orderDetails?.orderName ?? '',
        Header: 'Order name',
      },
      {
        id: 'voyage',
        accessor: (row) => {
          return row.order.assignment !== null
            ? row.order.shipmentByShipmentId?.name ||
                (row.order.shipmentId
                  ? `Shipment #${row.order.shipmentId}`
                  : `Order #${row.order.id}`)
            : null;
        },
        Header: 'Voyage ID',
      },
      {
        id: 'loadPort',
        accessor: (row) => row.order.loadPort?.name ?? '',
        Header: 'Load port',
      },
      {
        id: 'dischargePort',
        accessor: (row) => row.order.dischargePort?.name ?? '',
        Header: 'Discharge port',
      },
      {
        id: 'assortment',
        accessor: (row) => row.order.assortment?.name ?? '',
        Header: 'Commodity',
      },
      {
        id: 'amountPlanned',
        accessor: (row) => stringToNumOpt(row.order.cargoAmount),
        Header: 'Planned amount',
      },
      {
        id: 'amountPlannedUnit',
        accessor: (row) => row.order.cargoUnit?.name ?? '',
        Header: 'Planned amount unit',
        disableSortBy: true,
      },
      {
        id: 'volumeOnDeck',
        accessor: (row) =>
          row.isReadOnly
            ? null
            : stringToNumOpt(row.order.billOfLading?.volumeOnDeck ?? null),
        Header: 'Volume on deck',
        Cell: ({ value, row }: CellRendererProps<number | null>) => {
          const unit = row.original.order.billOfLading?.volumeUnit ?? null;
          return (
            <>
              {value
                ? `${value} ${unit ? volumeCapacityUnits[unit].symbol : ''}`
                : ''}
            </>
          );
        },
      },
      {
        id: 'massOnDeck',
        accessor: (row) =>
          row.isReadOnly
            ? null
            : stringToNumOpt(row.order.billOfLading?.weightOnDeck ?? null),
        Header: 'Mass on deck',
        Cell: ({ value, row }: CellRendererProps<number | null>) => {
          const unit = row.original.order.billOfLading?.weightUnit ?? null;
          return (
            <>
              {value
                ? `${value} ${unit ? massCapacityUnits[unit].symbol : ''}`
                : ''}
            </>
          );
        },
      },
      {
        id: 'measuredVolume',
        accessor: (row) =>
          row.isReadOnly
            ? null
            : stringToNumOpt(
                row.order.cargoMeasurement?.measuredVolume ?? null,
              ),
        Header: 'Measured volume',
        Cell: ({ value, row }: CellRendererProps<number | null>) => {
          const unit = row.original.order.cargoMeasurement?.volumeUnit ?? null;
          return (
            <>
              {value
                ? `${value} ${unit ? volumeCapacityUnits[unit].symbol : ''}`
                : ''}
            </>
          );
        },
      },
      {
        id: 'measuredMass',
        accessor: (row) =>
          row.isReadOnly
            ? null
            : stringToNumOpt(
                row.order.cargoMeasurement?.measuredWeight ?? null,
              ),
        Header: 'Measured mass',
        Cell: ({ value, row }: CellRendererProps<number | null>) => {
          const unit = row.original.order.cargoMeasurement?.weightUnit ?? null;
          return (
            <>
              {value
                ? `${value} ${unit ? massCapacityUnits[unit].symbol : ''}`
                : ''}
            </>
          );
        },
      },
      {
        id: 'bolWeight',
        accessor: (row) =>
          row.isReadOnly
            ? null
            : stringToNumOpt(row.order.billOfLading?.weight),
        Header: 'B/L Weight',
        Cell: ({ value, row }: CellRendererProps<number | null>) => {
          return (
            <>
              {value
                ? `${value} ${
                    getMassUnitName(row.original.order.billOfLading?.weightUnit)
                      ?.symbol ?? ''
                  }`
                : ''}
            </>
          );
        },
      },
      {
        id: 'bolVolume',
        accessor: (row) =>
          row.isReadOnly
            ? null
            : stringToNumOpt(row.order.billOfLading?.volume),
        Header: 'B/L Volume',
        Cell: ({ value, row }: CellRendererProps<number | null>) => {
          return (
            <>
              {value
                ? `${value} ${
                    getVolumeUnitName(
                      row.original.order.billOfLading?.volumeUnit,
                    )?.symbol ?? ''
                  }`
                : ''}
            </>
          );
        },
      },
      {
        id: 'chartererOrg',
        accessor: (row) => row.order.chartererOrg?.name ?? '',
        Header: 'Charterer organization',
      },
      {
        id: 'senderOrg',
        accessor: (row) => row.order.orderDetails?.senderOrg?.name ?? '',
        Header: 'Sending organization',
      },
      {
        id: 'receiverOrg',
        accessor: (row) => row.order.orderDetails?.receiverOrg?.name ?? '',
        Header: 'Receiving organization',
      },
      {
        id: 'shipownerOrg',
        accessor: (row) => row.order.assignment?.vessel?.ownerOrg?.name ?? '',
        Header: 'Shipowner organization',
      },
      {
        id: 'vessel',
        accessor: (row) => row.order.assignment?.vessel?.name ?? '',
        Header: 'Vessel',
      },
      {
        id: 'shipmentId',
        accessor: (row) => row.order.shipmentId ?? null,
        Header: 'Shipment ID',
      },
      {
        id: 'attributes',
        accessor: (row) =>
          row.order.orderAtributeAssociations.nodes
            ?.map((x) => x?.orderAttribute?.name ?? 'Unnamed Attribute')
            .join(', ') ?? '',
        Header: 'Attributes',
      },
      {
        id: 'arrivalLoadPort',
        accessor: (row) => {
          const arrival =
            row.order.loadPortPortCall?.plannedArrivalOverride ??
            row.order.loadPortPortCall?.plannedArrival;

          return dateStringToNumOpt(arrival);
        },
        Header: 'ETA/ATA Load port',
        Cell: ({ value, row }: CellRendererProps<number | null>) => {
          return (
            <>
              {portCallTimeCellContent(
                PortCallEventType.Arrival,
                true,
                value,
                row.original,
              )}
            </>
          );
        },
        getCellExportValue: (row) =>
          getDateExportValue(
            row.values.arrivalLoadPort,
            row.original.order.loadPort?.timezone ?? undefined,
          ),
        sortType: customPortSortType(
          CoreEventType.Load,
          PortCallEventType.Arrival,
        ),
      },
      {
        id: 'departureLoadPort',
        accessor: (row) => {
          const departure =
            row.order.loadPortPortCall?.plannedDepartureOverride ??
            row.order.loadPortPortCall?.plannedDeparture;
          return dateStringToNumOpt(departure);
        },
        Header: 'ETD/ATD Load port',
        Cell: ({ value, row }: CellRendererProps<number | null>) => {
          return (
            <>
              {portCallTimeCellContent(
                PortCallEventType.Departure,
                true,
                value,
                row.original,
              )}
            </>
          );
        },
        getCellExportValue: (row) =>
          getDateExportValue(
            row.values.departureLoadPort,
            row.original.order.loadPort?.timezone ?? undefined,
          ),
        sortType: customPortSortType(
          CoreEventType.Load,
          PortCallEventType.Departure,
        ),
      },
      {
        id: 'arrivalDischargePort',
        accessor: (row) => {
          const arrival =
            row.order.dischargePortPortCall?.plannedArrivalOverride ??
            row.order.dischargePortPortCall?.plannedArrival;

          return dateStringToNumOpt(arrival);
        },
        Header: 'ETA/ATA Discharge port',
        Cell: ({ value, row }: CellRendererProps<number | null>) => {
          return (
            <>
              {portCallTimeCellContent(
                PortCallEventType.Arrival,
                false,
                value,
                row.original,
              )}
            </>
          );
        },
        getCellExportValue: (row) =>
          getDateExportValue(
            row.values.arrivalDischargePort,
            row.original.order.dischargePort?.timezone ?? undefined,
          ),
        sortType: customPortSortType(
          CoreEventType.Discharge,
          PortCallEventType.Arrival,
        ),
      },
      {
        id: 'departureDischargePort',
        accessor: (row) => {
          const departure =
            row.order.dischargePortPortCall?.plannedDepartureOverride ??
            row.order.dischargePortPortCall?.plannedDeparture;
          return dateStringToNumOpt(departure);
        },
        Header: 'ETD/ATD Discharge port',
        Cell: ({ value, row }: CellRendererProps<number | null>) => {
          return (
            <>
              {portCallTimeCellContent(
                PortCallEventType.Departure,
                false,
                value,
                row.original,
              )}
            </>
          );
        },
        getCellExportValue: (row) =>
          getDateExportValue(
            row.values.departureDischargePort,
            row.original.order.dischargePort?.timezone ?? undefined,
          ),
        sortType: customPortSortType(
          CoreEventType.Discharge,
          PortCallEventType.Departure,
        ),
      },
      {
        Header: 'Contract name',
        accessor: (row) => getOrderContractName(row.order),
        Cell: ({ value }: CellRendererProps<string | null>) => (
          <Text maxWidth={200}>{value}</Text>
        ),
      },
      {
        Header: 'Remarks',
        accessor: (row) => {
          return (
            <Text maxWidth={200}>
              {row.order.orderDetails?.extraInfo ?? ''}
            </Text>
          );
        },
      },
      {
        id: 'status',
        accessor: (row) => {
          const dischargeSof =
            row.order.dischargePortPortCall?.statementOfFactsByPortCallId
              ?.nodes[0];

          return dischargeSof?.sailingTime || dischargeSof?.loadingCompleted
            ? 'completed'
            : row.order.assignment?.shared
              ? 'planned'
              : 'open';
        },
        Header: 'Status',
        Cell: ({ value }: CellRendererProps<'completed' | 'unknown'>) => (
          <Badge>{value}</Badge>
        ),
      },
    ],
    [],
  );
  const [orderKeyword, setOrderKeyword] = useLocalSetting('orderKeyword', '');
  const defaultGlobalFilter = useMemo<GlobalFilter>(
    () => ({
      fromDate: null,
      toDate: null,
      search: orderKeyword,
    }),
    [orderKeyword],
  );

  const globalFilterFn = useCallback(
    (
      rows: Array<Row<ReportingTableRowData>>,
      _columnIds: Array<string>,
      filterValue: GlobalFilter,
    ): Array<Row<ReportingTableRowData>> => {
      const filterWords =
        filterValue.search?.split(/\s+/).map((w) => w.toLowerCase()) ?? [];

      const from = filterValue.fromDate?.getTime() ?? null;
      const to = filterValue.toDate?.getTime() ?? null;
      const isInDateFilterRange = (time: number) =>
        (from ? time >= from : true) && (to ? time <= to : true);

      const result = rows
        .filter((row) => {
          return (
            isInDateFilterRange(row.values.arrivalLoadPort) ||
            isInDateFilterRange(row.values.arrivalDischargePort)
          );
        })
        .filter((row) => {
          return filterWords.every((word) => {
            return row.original.filterKeywords.some((val) =>
              val.includes(word),
            );
          });
        });

      return result;
    },
    [],
  );

  const [hiddenColumns, saveHiddenColumns] = useLocalSetting(
    'ordersTableHiddenColumns',
    [],
  );

  const [sortBy, saveSortBy] = useLocalSetting('ordersTableSortBy', []);

  const {
    allColumns,
    getTableBodyProps,
    getToggleHideAllColumnsProps,
    exportData,
    headerGroups,
    rows,
    setGlobalFilter,
    prepareRow,
    state: tableState,
  } = useTable<ReportingTableRowData>(
    {
      columns,
      data,
      getExportFileBlob,
      getExportFileName: ({ fileType, all }) =>
        `${moment().format('yyyy-MM-DD')}-${
          all ? 'all-orders' : 'filtered-orders'
        }.${fileType}`,
      globalFilter: globalFilterFn,
      initialState: {
        globalFilter: defaultGlobalFilter,
        sortBy,
        hiddenColumns,
      },
      autoResetGlobalFilter: false,
    },
    useGlobalFilter,
    useSortBy,
    useExportData,
  );

  useEffect(() => {
    saveHiddenColumns(tableState.hiddenColumns);
  }, [tableState.hiddenColumns, saveHiddenColumns]);

  useEffect(() => {
    saveSortBy(tableState.sortBy);
  }, [tableState.sortBy, saveSortBy]);

  const globalFilter = useMemo(
    () => tableState.globalFilter as GlobalFilter,
    [tableState.globalFilter],
  );

  return (
    <>
      <FilterBar>
        <Box flexDirection="row">
          {isArchiveViewAvailable && (
            <OptimisticCheckbox
              checked={showArchived}
              onChange={() => {
                setShowArchived(!showArchived);
              }}
              label="Include archived orders"
              labelColor="neutral.white_50"
            />
          )}
        </Box>
        <Box display="flex" flexDirection="row" alignItems="center">
          <FilterBarDatePicker
            label="From date"
            mr={3}
            minWidth={150}
            flexShrink={1}
            datePickerProps={{
              onChange: (d) =>
                setGlobalFilter({
                  ...globalFilter,
                  fromDate: d,
                }),
              selected: globalFilter.fromDate,
              showTime: false,
              defaultTimePickerType: 'START_OF_DAY',
              timeZone: 'UTC',
            }}
          />
          <FilterBarDatePicker
            label="To date"
            mr={3}
            minWidth={150}
            flexShrink={1}
            datePickerProps={{
              onChange: (d) =>
                setGlobalFilter({
                  ...globalFilter,
                  toDate: d,
                }),
              selected: globalFilter.toDate,
              showTime: false,
              defaultTimePickerType: 'END_OF_DAY',
              timeZone: 'UTC',
            }}
          />
          <SearchFilter
            globalFilter={globalFilter}
            setGlobalFilter={(v) => {
              setOrderKeyword(v.search ?? '');
              setGlobalFilter(v);
            }}
          />
          <FilterBarSeparator />
          <TableColumnsSelector
            allColumns={allColumns}
            getToggleHideAllColumnsProps={getToggleHideAllColumnsProps}
          />
          <DropdownButton minWidth={80} ml={3} label="Export">
            <Menu hasShadow>
              <MenuItem
                disabled={!exportData}
                label="CSV - Current view"
                onPress={() => exportData('csv', false)}
              />
              <MenuItem
                disabled={!exportData}
                label="CSV - All orders"
                onPress={() => exportData('csv', true)}
              />
              <MenuItem
                disabled={!exportData}
                label="Excel - Current view"
                onPress={() => exportData('xlsx', false)}
              />
              <MenuItem
                disabled={!exportData}
                label="Excel - All orders"
                onPress={() => exportData('xlsx', true)}
              />
            </Menu>
          </DropdownButton>
        </Box>
      </FilterBar>
      <Box
        display="flex"
        flex={1}
        overflow="auto"
        flexDirection="column"
        height="100%"
      >
        <Table height={rows.length ? 'auto' : '100%'}>
          <thead>
            {headerGroups.map((headerGroup) => (
              // eslint-disable-next-line react/jsx-key
              <TableHeaders isSticky {...headerGroup.getHeaderGroupProps()}>
                {headerGroup.headers.map((column) => (
                  // eslint-disable-next-line react/jsx-key
                  <TableHeader
                    {...column.getHeaderProps(column.getSortByToggleProps())}
                  >
                    {column.render('Header')}{' '}
                    {column.isSorted ? (column.isSortedDesc ? ' ▼' : ' ▲') : ''}
                  </TableHeader>
                ))}
              </TableHeaders>
            ))}
          </thead>
          <tbody {...getTableBodyProps}>
            {rows.length ? (
              rows.map((row, index) => {
                prepareRow(row);
                const { key, ...rowProps } = row.getRowProps();

                return (
                  <ReportingTableRow
                    row={row}
                    isActive={false}
                    key={key}
                    isMuted={index % 2 !== 0}
                    {...rowProps}
                  />
                );
              })
            ) : (
              <NotFoundTableRow
                colSpan={allColumns.length}
                isLoading={ordersLoading}
                isSearching={
                  !!data.length &&
                  Object.values(defaultGlobalFilter).some((x) => !!x)
                }
              />
            )}
          </tbody>
        </Table>
      </Box>
    </>
  );
};

const OrdersPage = (): JSX.Element => {
  const [showArchived, setShowArchived] = useState(false);
  const { data, loading } = useOrdersPageQuery({
    variables: {
      showArchived: showArchived ? undefined : false,
    },
  });

  const orders = useMemo(
    () => data?.orders?.nodes.filter(isDefined) ?? [],
    [data?.orders],
  );

  return (
    <BodyWrap>
      <ReportingTable
        ordersLoading={loading}
        orders={orders}
        showArchived={showArchived}
        setShowArchived={setShowArchived}
      />
    </BodyWrap>
  );
};

export default dynamic(() => Promise.resolve(OrdersPage), {
  ssr: false,
});
